import React from 'react';
import { isEmpty } from 'lodash';
import { addresses, arrays } from '@unite-us/client-utils';
import { SimpleSelectField } from '@unite-us/app-components';
import PropTypes from 'prop-types';
import { getProgramOptions } from '../utils';

function formatProviderDistance(receivingProvider, originCoordinates) {
  if (!receivingProvider.addresses?.length) return '';
  const primaryAddress = arrays.findPrimaryOrFirst(receivingProvider.addresses);
  const distanceObject = addresses.calculateDistance(primaryAddress, originCoordinates);
  return ` (${distanceObject.distance})`;
}

function getLegacyDraftPlaceholder(referral, index, originCoordinates) {
  const receivingProvider = referral.referrals?.[index]?.receiving_provider;
  if (!receivingProvider || referral.program) return null;
  const name = receivingProvider.name;
  const distance = formatProviderDistance(receivingProvider, originCoordinates);
  return `Unspecified program | ${name}${distance}`;
}

function getDisplayError(groupErrors, allowEmptyGroups, programField) {
  const groupError = !isEmpty(groupErrors) ? groupErrors[0] : null;
  const requiredError = !allowEmptyGroups && !programField.value ? 'Required' : null;
  return groupError || (programField.touched ? requiredError : null);
}

const ReferralProgramGroupSelect = (props) => {
  const {
    allowEmptyGroups,
    debouncedSearchNetworkGroups,
    hidden,
    index,
    onProgramGroupSelect,
    programField,
    selectedProgramIds,
    serviceAreaSupportForOrgsFlag,
    suggestedGroups,
    referral,
    originCoordinates,
    groupsOptionType,
    groupErrors,
  } = props;

  const onProgramSelect = (selected) => {
    onProgramGroupSelect(selected, index);
  };

  const placeHolderKey = serviceAreaSupportForOrgsFlag ? 'top' : 'closest';
  const legacyDraftPlaceholder = getLegacyDraftPlaceholder(referral, index, originCoordinates);
  const displayError = getDisplayError(groupErrors, allowEmptyGroups, programField);

  if (hidden) return null;

  return (
    <SimpleSelectField
      className={`referral-program-group-select${legacyDraftPlaceholder ? ' has-legacy-draft-placeholder' : ''}`}
      id={`select-field-program-group-${index}`}
      label="program-group-select"
      hideLabel
      value={programField.value}
      onChange={onProgramSelect}
      onInputChange={(search) => debouncedSearchNetworkGroups(search, groupsOptionType)}
      options={getProgramOptions({
        groups: suggestedGroups?.map((x, i) => ({ position: i + 1, ...x })),
        selectedProgramIds,
      })}
      optionSettings={{ labelKey: 'displayName', valueKey: 'id' }}
      placeholder={legacyDraftPlaceholder ?? 'Choose a program'}
      searchPlaceholder={`Displaying ${placeHolderKey} 50 - Type to search for more`}
      required={!allowEmptyGroups}
      error={displayError}
      searchable
    />
  );
};

ReferralProgramGroupSelect.propTypes = {
  allowEmptyGroups: PropTypes.bool,
  groupErrors: PropTypes.array,
  programField: PropTypes.object.isRequired,
  hidden: PropTypes.bool,
  index: PropTypes.number.isRequired,
  onProgramGroupSelect: PropTypes.func.isRequired,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  selectedProgramIds: PropTypes.array,
  suggestedGroups: PropTypes.array.isRequired,
  serviceAreaSupportForOrgsFlag: PropTypes.bool,
  referral: PropTypes.object.isRequired,
  originCoordinates: PropTypes.array.isRequired,
  groupsOptionType: PropTypes.oneOf(['in-network', 'out-of-network']).isRequired,
};

ReferralProgramGroupSelect.defaultProps = {
  hidden: false,
  selectedProgramIds: [],
  serviceAreaSupportForOrgsFlag: false,
  allowEmptyGroups: false,
  groupErrors: [],
};

export default ReferralProgramGroupSelect;
